// /////////////////////////////////////////////////////////////
// [ファイル] IsoDocumentPlatform.tsx
// [概要] ISO(QMS/ISMS 統合マネジメントシステム)文書プラットフォーム(フェーズ1)。
//        左=規格条項/自社文書ツリー、中央=条項本文、右=検索＋条項紐付け(双方向)。
//        Cloud SQL の mst_iso_*/trn_iso_* を Cloud Run(/api) 経由で参照する 3 ペイン閲覧UI。
//        「ISO27001 の A.5.1 に対応する自社規定は?」を検索/紐付けで辿れることを主眼とする。
// /////////////////////////////////////////////////////////////
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { blueGrey, indigo, teal, amber, green } from '@mui/material/colors';
import * as Icons from '../constants/icons';
import LoadingView from '../components/LoadingView';
import MarkdownMessage from '../components/MarkdownMessage';
import { useNotification } from '../providers/NotificationProvider';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import {
  useIsoDocuments,
  IsoClause,
  IsoSection,
  IsoMapping,
  IsoDocumentDetail,
  IsoSoaRow,
  IsoSoaMappedItem,
} from '../hooks/useIsoDocuments';

// ツリー描画の共通ノード形。規格条項(clause)/自社条項(section)を同じ構造に正規化する。
interface TreeNode {
  id: number;
  parentId: number | null;
  no: string;
  title: string;
  content?: string | null;
  order: number;
}

// 選択中アイテム(中央/右ペインの駆動)。kind で紐付け照会の向きを切り替える。
type Selected =
  | { kind: 'clause'; node: TreeNode }
  | { kind: 'section'; node: TreeNode }
  | null;

type Mode = 'standard' | 'document' | 'soa' | 'register';

const mgmtLabel = (v: string | null | undefined) =>
  v === 'integrated' ? '統合' : v === 'ISMS' ? 'ISMS' : v === 'QMS' ? 'QMS' : '—';
const docTypeLabel = (v: string | null | undefined) =>
  v === 'manual' ? 'マニュアル' : v === 'procedure' ? '規程/手順' : v === 'record' ? '記録' : v === 'standard_doc' ? '標準文書' : (v || '—');
const docStatusLabel = (v: string | null | undefined) =>
  v === 'obsolete' ? '旧' : v === 'active' ? '有効' : v === 'draft' ? '下書' : v === 'under_review' ? '確認中' : (v || '—');

// SoA 実施状況の選択肢
const SOA_STATUS: { value: string; label: string }[] = [
  { value: 'not_started', label: '未着手' },
  { value: 'planned', label: '計画中' },
  { value: 'partial', label: '一部実施' },
  { value: 'implemented', label: '実施済' },
];
const statusLabel = (v: string | null | undefined) => SOA_STATUS.find((s) => s.value === v)?.label || '—';
const applicableLabel = (v: boolean | null | undefined) => (v === true ? '適用' : v === false ? '除外' : '未判定');
// ギャップ = 除外でない(適用または未判定)のに対応自社規定が0件
const isGap = (r: IsoSoaRow) => r.applicable !== false && (r.mapped_count ?? 0) === 0;
// 対応自社規定の所属文書の Drive 閲覧URL(なければ null)
const driveUrl = (it: { drive_file_id?: string | null }) =>
  it.drive_file_id ? `https://drive.google.com/file/d/${it.drive_file_id}/view` : null;

/** parent_id からツリーを組み、展開集合に従って可視ノードを深さ付きで平坦化する。 */
function flattenVisible(
  nodes: TreeNode[],
  expanded: Set<number>
): { node: TreeNode; depth: number; hasChildren: boolean }[] {
  const idSet = new Set(nodes.map((n) => n.id));
  const byParent = new Map<number | null, TreeNode[]>();
  for (const n of nodes) {
    const key = n.parentId != null && idSet.has(n.parentId) ? n.parentId : null;
    const arr = byParent.get(key) ?? [];
    arr.push(n);
    byParent.set(key, arr);
  }
  for (const arr of byParent.values()) {
    arr.sort((a, b) => a.order - b.order || a.no.localeCompare(b.no));
  }
  const out: { node: TreeNode; depth: number; hasChildren: boolean }[] = [];
  const walk = (parent: number | null, depth: number) => {
    for (const n of byParent.get(parent) ?? []) {
      const kids = byParent.get(n.id) ?? [];
      out.push({ node: n, depth, hasChildren: kids.length > 0 });
      if (kids.length > 0 && expanded.has(n.id)) walk(n.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

const clauseToNode = (c: IsoClause): TreeNode => ({
  id: c.id,
  parentId: c.parent_id,
  no: c.clause_no,
  title: c.title || '',
  content: c.content,
  order: c.display_order,
});

const sectionToNode = (s: IsoSection): TreeNode => ({
  id: s.id,
  parentId: s.parent_id,
  no: s.section_no || '',
  title: s.title || '',
  content: s.content,
  order: s.display_order,
});

// 検索語が条項番号(例 A.5.1 / 6.1.2)か、フリーワードかを判定する。
const CLAUSE_NO_RE = /^(A\.)?\d+(\.\d+)*$/i;

export default function IsoDocumentPlatform() {
  const notify = useNotification();
  const {
    standards,
    documents,
    loading,
    error,
    loadClauses,
    loadDocumentDetail,
    searchSections,
    loadMappedClauses,
    loadSoa,
    upsertSoa,
  } = useIsoDocuments();

  const [mode, setMode] = useState<Mode>('standard');
  const [standardId, setStandardId] = useState<number | ''>('');
  const [documentId, setDocumentId] = useState<number | ''>('');
  const [clauses, setClauses] = useState<IsoClause[]>([]);
  const [detail, setDetail] = useState<IsoDocumentDetail | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [treeLoading, setTreeLoading] = useState(false);

  const [selected, setSelected] = useState<Selected>(null);
  const [mappings, setMappings] = useState<IsoMapping[]>([]);
  const [mapLoading, setMapLoading] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState<IsoSection[] | null>(null);
  const [searching, setSearching] = useState(false);

  // SoA / ギャップビュー
  const [soaRows, setSoaRows] = useState<IsoSoaRow[]>([]);
  const [soaLoading, setSoaLoading] = useState(false);
  const [gapOnly, setGapOnly] = useState(false);
  const [editRow, setEditRow] = useState<IsoSoaRow | null>(null);
  const [viewSection, setViewSection] = useState<IsoSoaMappedItem | null>(null);
  const [editForm, setEditForm] = useState<{ applicable: string; status: string; justification: string; control_owner: string; remarks: string }>({
    applicable: 'unset',
    status: '',
    justification: '',
    control_owner: '',
    remarks: '',
  });
  const [saving, setSaving] = useState(false);

  const refreshSoa = useCallback(() => {
    if (standardId === '') return;
    setSoaLoading(true);
    loadSoa(standardId)
      .then(setSoaRows)
      .finally(() => setSoaLoading(false));
  }, [standardId, loadSoa]);

  useEffect(() => {
    if (mode !== 'soa' || standardId === '') return;
    refreshSoa();
  }, [mode, standardId, refreshSoa]);

  // 起動後、規格/文書の既定選択を1件ずつ立てる
  useEffect(() => {
    if (standardId === '' && standards.length > 0) setStandardId(standards[0].id);
  }, [standards, standardId]);
  useEffect(() => {
    if (documentId === '' && documents.length > 0) setDocumentId(documents[0].id);
  }, [documents, documentId]);

  // 規格選択 → 条項ツリー取得(初期は最上位のみ展開)
  useEffect(() => {
    if (mode !== 'standard' || standardId === '') return;
    let alive = true;
    setTreeLoading(true);
    loadClauses(standardId)
      .then((rows) => {
        if (!alive) return;
        setClauses(rows);
        // 最上位(章)ノードは既定で展開しておく
        const roots = rows.filter((r) => r.parent_id == null).map((r) => r.id);
        setExpanded(new Set(roots));
      })
      .finally(() => alive && setTreeLoading(false));
    return () => {
      alive = false;
    };
  }, [mode, standardId, loadClauses]);

  // 文書選択 → 条項(section)ツリー取得
  useEffect(() => {
    if (mode !== 'document' || documentId === '') return;
    let alive = true;
    setTreeLoading(true);
    loadDocumentDetail(documentId)
      .then((d) => {
        if (!alive) return;
        setDetail(d);
        const roots = (d?.sections || []).filter((s) => s.parent_id == null).map((s) => s.id);
        setExpanded(new Set(roots));
      })
      .finally(() => alive && setTreeLoading(false));
    return () => {
      alive = false;
    };
  }, [mode, documentId, loadDocumentDetail]);

  // 選択アイテムの紐付けを双方向照会
  useEffect(() => {
    if (!selected) {
      setMappings([]);
      return;
    }
    let alive = true;
    setMapLoading(true);
    const arg = selected.kind === 'clause' ? { clauseId: selected.node.id } : { sectionId: selected.node.id };
    loadMappedClauses(arg)
      .then((rows) => alive && setMappings(rows))
      .finally(() => alive && setMapLoading(false));
    return () => {
      alive = false;
    };
  }, [selected, loadMappedClauses]);

  const treeNodes = useMemo<TreeNode[]>(
    () => (mode === 'standard' ? clauses.map(clauseToNode) : (detail?.sections || []).map(sectionToNode)),
    [mode, clauses, detail]
  );
  const visible = useMemo(() => flattenVisible(treeNodes, expanded), [treeNodes, expanded]);

  const toggleExpand = useCallback((id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectNode = useCallback(
    (node: TreeNode) => setSelected({ kind: mode === 'standard' ? 'clause' : 'section', node }),
    [mode]
  );

  const runSearch = useCallback(async () => {
    const q = searchInput.trim();
    if (!q) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const params = CLAUSE_NO_RE.test(q) ? { clauseNo: q.toUpperCase() } : { keyword: q };
      const rows = await searchSections(params);
      setSearchResults(rows);
      if (rows.length === 0) notify.info('該当する自社規定は見つかりませんでした');
    } catch {
      notify.error('検索に失敗しました');
    } finally {
      setSearching(false);
    }
  }, [searchInput, searchSections, notify]);

  const selectSearchResult = useCallback((s: IsoSection) => {
    setSelected({ kind: 'section', node: sectionToNode(s) });
  }, []);

  const openEdit = useCallback((row: IsoSoaRow) => {
    setEditRow(row);
    setEditForm({
      applicable: row.applicable === true ? 'yes' : row.applicable === false ? 'no' : 'unset',
      status: row.status || '',
      justification: row.justification || '',
      control_owner: row.control_owner || '',
      remarks: row.remarks || '',
    });
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editRow) return;
    setSaving(true);
    try {
      const applicable = editForm.applicable === 'yes' ? true : editForm.applicable === 'no' ? false : null;
      const id = await upsertSoa({
        clause_id: editRow.clause_id,
        applicable,
        status: editForm.status || null,
        justification: editForm.justification || null,
        control_owner: editForm.control_owner || null,
        remarks: editForm.remarks || null,
      });
      if (id) {
        notify.success('SoA を保存しました');
        setEditRow(null);
        refreshSoa();
      } else {
        notify.error('保存に失敗しました');
      }
    } catch {
      notify.error('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  }, [editRow, editForm, upsertSoa, notify, refreshSoa]);

  const soaSummary = useMemo(() => {
    const total = soaRows.length;
    const applied = soaRows.filter((r) => r.applicable === true).length;
    const excluded = soaRows.filter((r) => r.applicable === false).length;
    const gaps = soaRows.filter(isGap).length;
    const implemented = soaRows.filter((r) => r.status === 'implemented').length;
    return { total, applied, excluded, gaps, implemented };
  }, [soaRows]);

  const visibleSoa = useMemo(() => (gapOnly ? soaRows.filter(isGap) : soaRows), [soaRows, gapOnly]);

  // 文書台帳
  const [registerSearch, setRegisterSearch] = useState('');
  const [mgmtFilter, setMgmtFilter] = useState<string>('all');
  const [hideObsolete, setHideObsolete] = useState(true);
  const visibleDocs = useMemo(() => {
    const w = registerSearch.trim().toLowerCase();
    return documents.filter((d) => {
      if (hideObsolete && d.status === 'obsolete') return false;
      if (mgmtFilter !== 'all' && (d.mgmt_system || '') !== mgmtFilter) return false;
      if (w) {
        const hay = `${d.title} ${d.source_clause_no || ''} ${d.owner_name || ''} ${d.source_group || ''}`.toLowerCase();
        if (!hay.includes(w)) return false;
      }
      return true;
    });
  }, [documents, registerSearch, mgmtFilter, hideObsolete]);

  const handleModeChange = (_: unknown, next: Mode | null) => {
    if (!next || next === mode) return;
    setMode(next);
    setSelected(null);
  };

  if (loading) return <LoadingView message="ISO文書を読み込み中..." height="80vh" />;

  return (
    <Box sx={{ p: 3, width: '98%', margin: '0 auto', boxSizing: 'border-box' }}>
      {/* ヘッダ */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: 1, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5, color: blueGrey[600] }}>
            <Icons.FactCheck /> ISO文書管理
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            規格条項と自社規定・マニュアルを一元管理し、条項の対応関係(適合性)を辿れます
          </Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={handleModeChange} sx={{ '& .MuiToggleButton-root': { fontSize: '0.78rem', py: 0.4, px: 1.4 } }}>
          <ToggleButton value="standard">
            <Icons.CollectionsBookmark sx={{ fontSize: 16, mr: 0.6 }} /> 規格条項
          </ToggleButton>
          <ToggleButton value="document">
            <Icons.Description sx={{ fontSize: 16, mr: 0.6 }} /> 自社文書
          </ToggleButton>
          <ToggleButton value="soa">
            <Icons.FactCheck sx={{ fontSize: 16, mr: 0.6 }} /> SoA / ギャップ
          </ToggleButton>
          <ToggleButton value="register">
            <Icons.ListAlt sx={{ fontSize: 16, mr: 0.6 }} /> 文書台帳
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Typography variant="caption" color="error" sx={{ px: 1, display: 'block', mt: 1 }}>
          {error}
        </Typography>
      )}

      {/* 3 ペイン (規格条項 / 自社文書モード) */}
      {(mode === 'standard' || mode === 'document') && (
      <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, height: 'calc(100vh - 190px)' }}>
        {/* 左: ツリー */}
        <Paper variant="outlined" sx={{ width: 330, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            {mode === 'standard' ? (
              <Select
                size="small"
                fullWidth
                value={standardId === '' ? '' : String(standardId)}
                onChange={(e) => setStandardId(Number(e.target.value))}
                sx={{ fontSize: '0.8rem' }}
              >
                {standards.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)} sx={{ fontSize: '0.8rem' }}>
                    {s.standard_code}{s.edition ? `:${s.edition}` : ''}（条項{s.clause_count ?? 0}）
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Select
                size="small"
                fullWidth
                value={documentId === '' ? '' : String(documentId)}
                onChange={(e) => setDocumentId(Number(e.target.value))}
                sx={{ fontSize: '0.8rem' }}
              >
                {documents.map((d) => (
                  <MenuItem key={d.id} value={String(d.id)} sx={{ fontSize: '0.8rem' }}>
                    {d.doc_code ? `${d.doc_code} ` : ''}{d.title}
                  </MenuItem>
                ))}
              </Select>
            )}
          </Box>
          <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
            {treeLoading ? (
              <LoadingView message="読み込み中..." height="40vh" size={36} />
            ) : visible.length === 0 ? (
              <Box sx={{ p: 2, color: 'text.disabled' }}>
                <Typography variant="body2">条項がありません</Typography>
              </Box>
            ) : (
              visible.map(({ node, depth, hasChildren }) => {
                const isSel = selected?.node.id === node.id && selected?.kind === (mode === 'standard' ? 'clause' : 'section');
                return (
                  <Box
                    key={node.id}
                    onClick={() => selectNode(node)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      pl: 0.5 + depth * 1.5,
                      pr: 1,
                      py: 0.5,
                      cursor: 'pointer',
                      bgcolor: isSel ? indigo[50] : 'transparent',
                      borderLeft: isSel ? `3px solid ${indigo[400]}` : '3px solid transparent',
                      '&:hover': { bgcolor: isSel ? indigo[50] : blueGrey[50] },
                    }}
                  >
                    {hasChildren ? (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpand(node.id);
                        }}
                        sx={{ p: 0.1 }}
                      >
                        {expanded.has(node.id) ? (
                          <Icons.ExpandMore sx={{ fontSize: 18 }} />
                        ) : (
                          <Icons.ChevronRight sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    ) : (
                      <Box sx={{ width: 20, flexShrink: 0 }} />
                    )}
                    {node.no && (
                      <Typography variant="caption" sx={{ fontWeight: 700, color: blueGrey[700], flexShrink: 0 }}>
                        {node.no}
                      </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {node.title}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>
        </Paper>

        {/* 中央: 本文 */}
        <Paper variant="outlined" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden' }}>
          {selected ? (
            <Box sx={{ p: 2.5, overflowY: 'auto' }}>
              <Chip
                size="small"
                label={selected.kind === 'clause' ? '規格条項' : '自社規定'}
                sx={{
                  fontSize: '0.7rem',
                  height: 20,
                  mb: 1,
                  bgcolor: selected.kind === 'clause' ? teal[50] : indigo[50],
                  color: selected.kind === 'clause' ? teal[800] : indigo[800],
                }}
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', gap: 1, alignItems: 'baseline' }}>
                {selected.node.no && <span style={{ color: blueGrey[700] }}>{selected.node.no}</span>}
                {selected.node.title}
              </Typography>
              <Divider sx={{ my: 1.5 }} />
              {selected.node.content ? (
                <Box sx={{ color: 'text.primary' }}>
                  <MarkdownMessage>{selected.node.content}</MarkdownMessage>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                  本文(content)は未整備です。{selected.kind === 'clause' ? '正本(対訳規格)入手後に校正予定です。' : ''}
                </Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
              <Typography variant="body2">左のツリー、または右の検索結果から項目を選択してください</Typography>
            </Box>
          )}
        </Paper>

        {/* 右: 検索 + 紐付け */}
        <Paper variant="outlined" sx={{ width: 360, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden' }}>
          {/* 検索 */}
          <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="キーワード / 条項番号(例 A.5.1)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') runSearch();
              }}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.6 } }}
              InputProps={{
                startAdornment: <Icons.Search fontSize="small" sx={{ mr: 0.5, color: 'text.disabled' }} />,
                endAdornment: searchInput ? (
                  <IconButton size="small" onClick={() => { setSearchInput(''); setSearchResults(null); }} sx={{ p: 0.2 }}>
                    <Icons.Close sx={{ fontSize: 16 }} />
                  </IconButton>
                ) : null,
              }}
            />
          </Box>
          <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
            {/* 検索結果 */}
            {searchResults !== null && (
              <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: blueGrey[50] }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: blueGrey[700], display: 'block', mb: 0.5 }}>
                  検索結果（{searching ? '…' : `${searchResults.length}件`}）
                </Typography>
                {searchResults.map((s) => (
                  <Box
                    key={s.id}
                    onClick={() => selectSearchResult(s)}
                    sx={{ px: 1, py: 0.5, cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'white' } }}
                  >
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                      {s.section_no ? `${s.section_no} ` : ''}{s.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.doc_code || s.document_title}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* 紐付け(双方向) */}
            <Box sx={{ p: 1.2 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: blueGrey[700], display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Icons.SwapHoriz sx={{ fontSize: 16 }} />
                {selected?.kind === 'clause'
                  ? 'この条項に対応する自社規定'
                  : selected?.kind === 'section'
                  ? 'この規定が満たす規格条項'
                  : '条項紐付け'}
              </Typography>
              {!selected ? (
                <Typography variant="caption" color="text.disabled">
                  項目を選択すると対応関係を表示します
                </Typography>
              ) : mapLoading ? (
                <Typography variant="caption" color="text.disabled">
                  読み込み中...
                </Typography>
              ) : mappings.length === 0 ? (
                <Typography variant="caption" color="text.disabled">
                  紐付けはありません
                </Typography>
              ) : (
                mappings.map((m) => (
                  <Box
                    key={m.mapping_id}
                    sx={{ px: 1, py: 0.7, mb: 0.5, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'white' }}
                  >
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                      {selected.kind === 'clause'
                        ? `${m.section_no ? `${m.section_no} ` : ''}${m.title || ''}`
                        : `${m.clause_no ? `${m.clause_no} ` : ''}${m.title || ''}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {selected.kind === 'clause'
                        ? m.doc_code || m.document_title
                        : m.standard_code}
                    </Typography>
                    {m.note && (
                      <Typography variant="caption" sx={{ display: 'block', color: teal[700], mt: 0.3 }}>
                        {m.note}
                      </Typography>
                    )}
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
      )}

      {/* SoA / ギャップビュー */}
      {mode === 'soa' && (
        <Box sx={{ mt: 1.5 }}>
          {/* コントロール + サマリ */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, flexWrap: 'wrap' }}>
            <Select
              size="small"
              value={standardId === '' ? '' : String(standardId)}
              onChange={(e) => setStandardId(Number(e.target.value))}
              sx={{ fontSize: '0.8rem', minWidth: 240 }}
            >
              {standards.map((s) => (
                <MenuItem key={s.id} value={String(s.id)} sx={{ fontSize: '0.8rem' }}>
                  {s.standard_code}{s.edition ? `:${s.edition}` : ''}
                </MenuItem>
              ))}
            </Select>
            <FormControlLabel
              control={<Switch size="small" checked={gapOnly} onChange={(e) => setGapOnly(e.target.checked)} />}
              label={<Typography variant="caption">未対応(ギャップ)のみ</Typography>}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
              <Chip size="small" label={`全 ${soaSummary.total}`} sx={{ height: 22, fontSize: '0.72rem', bgcolor: blueGrey[50] }} />
              <Chip size="small" label={`適用 ${soaSummary.applied}`} sx={{ height: 22, fontSize: '0.72rem', bgcolor: green[50], color: green[800] }} />
              <Chip size="small" label={`除外 ${soaSummary.excluded}`} sx={{ height: 22, fontSize: '0.72rem', bgcolor: blueGrey[100], color: blueGrey[700] }} />
              <Chip size="small" label={`実施済 ${soaSummary.implemented}`} sx={{ height: 22, fontSize: '0.72rem', bgcolor: teal[50], color: teal[800] }} />
              <Chip size="small" label={`未対応 ${soaSummary.gaps}`} sx={{ height: 22, fontSize: '0.72rem', bgcolor: amber[100], color: amber[900] }} />
            </Box>
          </Box>

          <Paper variant="outlined" sx={{ mt: 1, borderRadius: 2, height: 'calc(100vh - 250px)', overflow: 'auto' }}>
            {soaLoading ? (
              <LoadingView message="SoA を読み込み中..." height="50vh" size={40} />
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { fontSize: '0.72rem', fontWeight: 700, color: blueGrey[700], bgcolor: blueGrey[50], py: 0.6 } }}>
                    <TableCell>管理策</TableCell>
                    <TableCell>カテゴリ</TableCell>
                    <TableCell>適用</TableCell>
                    <TableCell>実施状況</TableCell>
                    <TableCell>対応自社規定</TableCell>
                    <TableCell>理由 / 備考</TableCell>
                    <TableCell align="right">編集</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleSoa.map((r) => {
                    const gap = isGap(r);
                    return (
                      <TableRow key={r.clause_id} hover sx={{ '& td': { py: 0.5 }, bgcolor: gap ? amber[50] : 'inherit' }}>
                        <TableCell sx={{ maxWidth: 300 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: blueGrey[800] }}>{r.clause_no}</Typography>{' '}
                          <Typography variant="caption">{r.title}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">{r.category_title}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={applicableLabel(r.applicable)}
                            sx={{
                              height: 18,
                              fontSize: '0.68rem',
                              bgcolor: r.applicable === true ? green[50] : r.applicable === false ? blueGrey[100] : 'transparent',
                              color: r.applicable === true ? green[800] : blueGrey[600],
                              border: r.applicable == null ? `1px solid ${blueGrey[100]}` : 'none',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{statusLabel(r.status)}</Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 340 }}>
                          {r.mapped_count > 0 ? (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                              {r.mapped_items.map((it) => {
                                const url = driveUrl(it);
                                return (
                                  <Box key={it.section_id} sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                    <Tooltip
                                      title={
                                        (it.document_title || '') +
                                        (it.content ? ` — ${it.content.length > 120 ? it.content.slice(0, 120) + '…' : it.content}` : '')
                                      }
                                    >
                                      <Chip
                                        size="small"
                                        label={`${it.doc_code ? `[${it.doc_code}] ` : ''}${it.title || ''}`}
                                        onClick={() => setViewSection(it)}
                                        sx={{
                                          height: 20,
                                          maxWidth: 300,
                                          fontSize: '0.68rem',
                                          bgcolor: teal[50],
                                          color: teal[800],
                                          cursor: 'pointer',
                                          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                                        }}
                                      />
                                    </Tooltip>
                                    {url && (
                                      <Tooltip title="該当ファイルを開く">
                                        <IconButton
                                          size="small"
                                          component="a"
                                          href={url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          sx={{ p: 0.2, ml: 0.1 }}
                                        >
                                          <Icons.OpenInNew sx={{ fontSize: 14, color: blueGrey[500] }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : (
                            <Typography variant="caption" sx={{ color: gap ? amber[900] : 'text.disabled', fontWeight: gap ? 700 : 400 }}>
                              {gap ? '未対応' : '—'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 240 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                            {r.justification || r.remarks || ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEdit(r)} sx={{ p: 0.3 }}>
                            <Icons.Edit sx={{ fontSize: 16 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Box>
      )}

      {/* 文書台帳ビュー */}
      {mode === 'register' && (
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="文書名 / 条項 / 担当で絞り込み"
              value={registerSearch}
              onChange={(e) => setRegisterSearch(e.target.value)}
              sx={{ minWidth: 280, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.5 } }}
              InputProps={{ startAdornment: <Icons.Search fontSize="small" sx={{ mr: 0.5, color: 'text.disabled' }} /> }}
            />
            <Select size="small" value={mgmtFilter} onChange={(e) => setMgmtFilter(e.target.value)} sx={{ fontSize: '0.8rem', minWidth: 130 }}>
              <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>すべての規格</MenuItem>
              <MenuItem value="ISMS" sx={{ fontSize: '0.8rem' }}>ISMS</MenuItem>
              <MenuItem value="QMS" sx={{ fontSize: '0.8rem' }}>QMS</MenuItem>
              <MenuItem value="integrated" sx={{ fontSize: '0.8rem' }}>統合</MenuItem>
            </Select>
            <FormControlLabel
              control={<Switch size="small" checked={hideObsolete} onChange={(e) => setHideObsolete(e.target.checked)} />}
              label={<Typography variant="caption">旧版を隠す</Typography>}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Chip size="small" label={`表示 ${visibleDocs.length} / 全 ${documents.length}`} sx={{ height: 22, fontSize: '0.72rem', bgcolor: blueGrey[50] }} />
          </Box>

          <Paper variant="outlined" sx={{ mt: 1, borderRadius: 2, height: 'calc(100vh - 250px)', overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontSize: '0.72rem', fontWeight: 700, color: blueGrey[700], bgcolor: blueGrey[50], py: 0.6 } }}>
                  <TableCell>文書名</TableCell>
                  <TableCell>規格</TableCell>
                  <TableCell>条項</TableCell>
                  <TableCell>種別</TableCell>
                  <TableCell>状態</TableCell>
                  <TableCell>担当</TableCell>
                  <TableCell align="right">開く</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleDocs.map((d) => (
                  <TableRow key={d.id} hover sx={{ '& td': { py: 0.5 }, opacity: d.status === 'obsolete' ? 0.55 : 1 }}>
                    <TableCell sx={{ maxWidth: 460 }}>
                      <Typography variant="caption" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                        {d.source_group ? <span style={{ color: blueGrey[400] }}>{d.source_group} </span> : ''}
                        {d.title}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption">{mgmtLabel(d.mgmt_system)}</Typography></TableCell>
                    <TableCell><Typography variant="caption" sx={{ fontWeight: 700, color: blueGrey[700] }}>{d.source_clause_no || '—'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{docTypeLabel(d.doc_type)}</Typography></TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={docStatusLabel(d.status)}
                        sx={{ height: 18, fontSize: '0.66rem', bgcolor: d.status === 'obsolete' ? blueGrey[100] : green[50], color: d.status === 'obsolete' ? blueGrey[600] : green[800] }}
                      />
                    </TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{d.owner_name || '—'}</Typography></TableCell>
                    <TableCell align="right">
                      {d.web_view_link ? (
                        <Tooltip title="ファイルを開く">
                          <IconButton size="small" component="a" href={d.web_view_link} target="_blank" rel="noopener noreferrer" sx={{ p: 0.3 }}>
                            <Icons.OpenInNew sx={{ fontSize: 16, color: blueGrey[500] }} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      {/* SoA 行編集ダイアログ */}
      <Dialog open={!!editRow} onClose={() => setEditRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '0.9rem', py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icons.FactCheck sx={{ fontSize: 18 }} />
          SoA 編集　{editRow?.clause_no} {editRow?.title}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                select
                size="small"
                label="適用可否"
                value={editForm.applicable}
                onChange={(e) => setEditForm((f) => ({ ...f, applicable: e.target.value }))}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="yes">適用</MenuItem>
                <MenuItem value="no">除外</MenuItem>
                <MenuItem value="unset">未判定</MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                label="実施状況"
                value={editForm.status}
                onChange={(e) => setEditForm((f) => ({ ...f, status: e.target.value }))}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="">—</MenuItem>
                {SOA_STATUS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </TextField>
            </Box>
            <TextField
              size="small"
              label="選定 / 除外理由"
              value={editForm.justification}
              onChange={(e) => setEditForm((f) => ({ ...f, justification: e.target.value }))}
              multiline
              minRows={2}
              fullWidth
            />
            <TextField
              size="small"
              label="管理策の責任者"
              value={editForm.control_owner}
              onChange={(e) => setEditForm((f) => ({ ...f, control_owner: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              label="備考"
              value={editForm.remarks}
              onChange={(e) => setEditForm((f) => ({ ...f, remarks: e.target.value }))}
              multiline
              minRows={2}
              fullWidth
            />
            {editRow && editRow.mapped_count > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  対応自社規定（クリックで本文表示）
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                  {editRow.mapped_items.map((it) => (
                    <Chip
                      key={it.section_id}
                      size="small"
                      label={`${it.doc_code ? `[${it.doc_code}] ` : ''}${it.title || ''}`}
                      onClick={() => setViewSection(it)}
                      sx={{ height: 20, fontSize: '0.68rem', bgcolor: teal[50], color: teal[800], cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)} sx={STANDARD_TEXT_BUTTON_SX}>
            キャンセル
          </Button>
          <Button onClick={saveEdit} disabled={saving} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX}>
            保存する
          </Button>
        </DialogActions>
      </Dialog>

      {/* 対応自社規定の本文ドリルダウン */}
      <Dialog open={!!viewSection} onClose={() => setViewSection(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '0.9rem', py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icons.Description sx={{ fontSize: 18 }} />
          {viewSection?.document_title}
          {viewSection?.doc_code ? `（${viewSection.doc_code}）` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, display: 'flex', gap: 1, alignItems: 'baseline' }}>
            {viewSection?.section_no && <span style={{ color: blueGrey[700] }}>{viewSection.section_no}</span>}
            {viewSection?.title}
          </Typography>
          {viewSection?.content ? (
            <Box sx={{ color: 'text.primary' }}>
              <MarkdownMessage>{viewSection.content}</MarkdownMessage>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
              本文(content)は未整備です。
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {viewSection && driveUrl(viewSection) ? (
            <Button
              component="a"
              href={driveUrl(viewSection) as string}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Icons.OpenInNew />}
              sx={STANDARD_TEXT_BUTTON_SX}
            >
              該当ファイルを開く
            </Button>
          ) : (
            <Typography variant="caption" color="text.disabled" sx={{ pl: 1 }}>
              {viewSection?.gcs_path ? '原本は Cloud Storage 保管' : 'ファイル参照なし'}
            </Typography>
          )}
          <Button onClick={() => setViewSection(null)} sx={STANDARD_TEXT_BUTTON_SX}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
